import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, setDoc } from 'firebase/firestore';
import { ArrowLeft, Copy, Save, UserPlus } from 'lucide-react';

const emptyForm = {
  name: '',
  email: '',
  firebaseUid: '',
  role: 'sales',
  status: 'active',
  department: '',
  designation: '',
  branch: '',
  region: ''
};

const roleOptions = [
  { value: 'sales', label: 'Sales' },
  { value: 'sales_manager', label: 'Sales Manager' },
  { value: 'ops', label: 'Operations' },
  { value: 'ops_manager', label: 'Operations Manager' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'accounts', label: 'Accounts' },
  { value: 'admin', label: 'Admin' }
];

const AdminEmployeeCreate = ({ db }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [employeeId, setEmployeeId] = useState('');

  useEffect(() => {
    const employeeRef = doc(collection(db, 'employees'));
    setEmployeeId(employeeRef.id);
  }, [db]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!String(formData.name || '').trim() || !String(formData.email || '').trim()) {
      alert('Name and email are required.');
      return;
    }

    setIsSaving(true);
    try {
      const employeeRef = doc(db, 'employees', employeeId || doc(collection(db, 'employees')).id);
      const resolvedEmployeeId = employeeRef.id;
      const timestamp = new Date().toISOString();

      await setDoc(employeeRef, {
        employeeId: resolvedEmployeeId,
        name: String(formData.name || '').trim(),
        email: String(formData.email || '').trim().toLowerCase(),
        firebaseUid: String(formData.firebaseUid || '').trim(),
        role: String(formData.role || 'sales').trim(),
        status: String(formData.status || 'active').trim(),
        department: String(formData.department || '').trim(),
        designation: String(formData.designation || '').trim(),
        branch: String(formData.branch || '').trim(),
        region: String(formData.region || '').trim(),
        createdAt: timestamp,
        updatedAt: timestamp
      });

      navigate('/admin/employees');
    } catch (error) {
      console.error('Error creating employee:', error);
      alert(`Error creating employee: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '980px', margin: '0 auto' }}>
      <div style={headerStyle}>
        <button onClick={() => navigate('/admin/employees')} style={backButtonStyle} aria-label="Back to employees">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>
            Add Employee
          </h1>
          <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '14px' }}>
            Create a new employee record for ownership and HR mapping.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <section style={cardStyle}>
          <div style={cardHeaderStyle}>
            <UserPlus size={18} />
            Basic Details
          </div>
          <div style={gridStyle}>
            <Field label="Employee ID">
              <div style={readOnlyFieldStyle}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{employeeId || 'Generating...'}</span>
                <Copy size={14} color="#94a3b8" />
              </div>
            </Field>
            <Field label="Employee Name" required>
              <input name="name" value={formData.name} onChange={handleChange} style={inputStyle} required />
            </Field>
            <Field label="Email" required>
              <input type="email" name="email" value={formData.email} onChange={handleChange} style={inputStyle} required />
            </Field>
            <Field label="Firebase UID">
              <input name="firebaseUid" value={formData.firebaseUid} onChange={handleChange} style={inputStyle} placeholder="Optional for now" />
            </Field>
            <Field label="Role" required>
              <select name="role" value={formData.role} onChange={handleChange} style={inputStyle} required>
                {roleOptions.map((role) => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Status" required>
              <select name="status" value={formData.status} onChange={handleChange} style={inputStyle} required>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </Field>
          </div>
        </section>

        <section style={cardStyle}>
          <div style={cardHeaderStyle}>Organization</div>
          <div style={gridStyle}>
            <Field label="Department">
              <input name="department" value={formData.department} onChange={handleChange} style={inputStyle} />
            </Field>
            <Field label="Designation">
              <input name="designation" value={formData.designation} onChange={handleChange} style={inputStyle} />
            </Field>
            <Field label="Branch">
              <input name="branch" value={formData.branch} onChange={handleChange} style={inputStyle} />
            </Field>
            <Field label="Region">
              <input name="region" value={formData.region} onChange={handleChange} style={inputStyle} />
            </Field>
          </div>
        </section>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button type="button" onClick={() => navigate('/admin/employees')} style={secondaryBtnStyle}>
            Cancel
          </button>
          <button type="submit" disabled={isSaving} style={primaryBtnStyle}>
            <Save size={18} />
            {isSaving ? 'Saving...' : 'Create Employee'}
          </button>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label style={fieldStyle}>
    <span style={labelStyle}>
      {label}{required ? ' *' : ''}
    </span>
    {children}
  </label>
);

const headerStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '14px',
  marginBottom: '20px'
};

const backButtonStyle = {
  width: '40px',
  height: '40px',
  borderRadius: '10px',
  border: '1px solid #dbe3ee',
  backgroundColor: 'white',
  color: '#334155',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer'
};

const cardStyle = {
  backgroundColor: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
};

const cardHeaderStyle = {
  padding: '14px 18px',
  borderBottom: '1px solid #e2e8f0',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '14px',
  fontWeight: '800',
  color: '#0f172a',
  backgroundColor: '#f8fafc'
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '16px',
  padding: '18px'
};

const fieldStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px'
};

const labelStyle = {
  fontSize: '11px',
  fontWeight: '700',
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const inputStyle = {
  width: '100%',
  padding: '11px 12px',
  borderRadius: '10px',
  border: '1px solid #d1d5db',
  backgroundColor: 'white',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box'
};

const readOnlyFieldStyle = {
  width: '100%',
  minHeight: '44px',
  padding: '11px 12px',
  borderRadius: '10px',
  border: '1px solid #dbe3ee',
  backgroundColor: '#f8fafc',
  color: '#334155',
  fontSize: '14px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  boxSizing: 'border-box'
};

const primaryBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 18px',
  borderRadius: '10px',
  border: 'none',
  backgroundColor: '#2563eb',
  color: 'white',
  fontSize: '14px',
  fontWeight: '700',
  cursor: 'pointer'
};

const secondaryBtnStyle = {
  padding: '12px 18px',
  borderRadius: '10px',
  border: '1px solid #d1d5db',
  backgroundColor: 'white',
  color: '#334155',
  fontSize: '14px',
  fontWeight: '700',
  cursor: 'pointer'
};

export default AdminEmployeeCreate;
